import { cn } from '@lyearn/style';

import { LinearProgressProps } from '../types';
import { getProgressColorClasses } from '../utils';
import { setTranslateXPercent } from './styles';

interface GetProgressBarStylesParams {
  index: number;
  segments: number;
  value: number;
}

export const getProgressBarStyles = (params: GetProgressBarStylesParams) => {
  const { index, segments, value } = params;

  const minValue = (100 / segments) * index;
  const maxValue = (100 / segments) * (index + 1);

  let width = 0;

  if (value >= maxValue) {
    width = 100;
  } else if (value > minValue && value < maxValue) {
    width = (value - minValue) * segments;
  }

  return width ? setTranslateXPercent(document.dir === 'rtl' ? 100 - width : width - 100) : null;
};

interface GetLinearProgressSegmentClassesParams {
  color: NonNullable<LinearProgressProps['color']>;
  index: number;
  overrides: LinearProgressProps['overrides'];
  segments: number;
}

const classes = {
  bar: 'bg-[currentColor] absolute top-0 start-0 w-full h-full',
  root: 'relative overflow-hidden w-full h-full',
  transition: 'transition ease-linear duration-200',
};

export const getLinearProgressSegmentClasses = (params: GetLinearProgressSegmentClassesParams) => {
  const { color, index, overrides, segments } = params;
  const { progress, track } = getProgressColorClasses(color);

  const hasMultipleSegments = segments > 1;

  const roundedClasses = cn(
    index === 0 && (overrides?.borderRadius?.left ?? 'rounded-s-full'),
    index === segments - 1 && (overrides?.borderRadius?.right ?? 'rounded-e-full'),
  );

  const progressColor = overrides?.progressColor ?? progress;
  const trackColor = overrides?.trackColor ?? track;
  const transition = overrides?.transition ?? classes.transition;

  return {
    progress: cn(classes.bar, progressColor, {
      [roundedClasses]: !hasMultipleSegments,
      [transition]: !hasMultipleSegments,
    }),
    root: cn(classes.root, roundedClasses),
    track: cn(classes.bar, roundedClasses, trackColor),
  };
};
